import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { X, Database, Phone, Check, ExternalLink, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const IntegrationSetupModal = ({ integration, onClose, onSuccess }) => {
  const [apiKey, setApiKey] = useState('');
  const [agentId, setAgentId] = useState('');
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  const integrationConfig = {
    fub: {
      title: 'Follow Up Boss CRM',
      icon: Database,
      color: 'green',
      fields: [
        {
          label: 'FUB API Key',
          placeholder: 'fka_xxxxxxxxxxxxxxxxxxxxxxx',
          value: apiKey,
          onChange: setApiKey,
          type: 'password',
          required: true
        }
      ],
      instructions: [
        'Log in to Follow Up Boss',
        'Go to Settings → Integrations → API',
        'Click "Create API Key"',
        'Copy the key and paste it above'
      ],
      docsLink: 'https://docs.followupboss.com/reference/authentication'
    },
    retell: {
      title: 'Retell AI Voice',
      icon: Phone,
      color: 'purple',
      fields: [
        {
          label: 'Retell API Key',
          placeholder: 'key_xxxxxxxxxxxxxxxxxxxxxxx',
          value: apiKey,
          onChange: setApiKey,
          type: 'password',
          required: true
        },
        {
          label: 'Agent ID (Optional)',
          placeholder: 'agent_xxxxxxxxxxxxxxx',
          value: agentId,
          onChange: setAgentId,
          type: 'text',
          required: false
        }
      ],
      instructions: [
        'Sign up at app.retellai.com',
        'Go to Settings → API Keys',
        'Generate new API key',
        'Copy your Agent ID from Agents page (optional)',
        'Paste both above'
      ],
      docsLink: 'https://docs.retellai.com'
    }
  };

  const config = integrationConfig[integration];
  const Icon = config.icon;

  const handleSave = async () => {
    setLoading(true);
    setSaved(false);

    try {
      const endpoint = integration === 'fub' 
        ? `${BACKEND_URL}/api/user/integrations/fub`
        : `${BACKEND_URL}/api/user/integrations/retell`;

      const payload = integration === 'fub'
        ? { api_key: apiKey }
        : { api_key: apiKey, agent_id: agentId };

      await axios.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setSaved(true);
      toast.success(`${config.title} connected successfully!`);
      
      // Call onSuccess after a brief delay to show success state
      setTimeout(() => {
        onSuccess();
      }, 1500);

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection failed. Please check your API key.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveForLater = () => {
    // Save draft state to localStorage
    const draftKey = `integration_draft_${integration}`;
    localStorage.setItem(draftKey, JSON.stringify({ apiKey, agentId, savedAt: new Date().toISOString() }));
    toast.info('Draft saved! You can continue setup later.');
    onClose();
  };

  // Load draft on mount
  React.useEffect(() => {
    const draftKey = `integration_draft_${integration}`;
    const draft = localStorage.getItem(draftKey);
    if (draft) {
      try {
        const { apiKey: savedApiKey, agentId: savedAgentId } = JSON.parse(draft);
        if (savedApiKey) setApiKey(savedApiKey);
        if (savedAgentId) setAgentId(savedAgentId);
      } catch (e) {
        console.error('Failed to load draft:', e);
      }
    }
  }, [integration]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-2xl bg-white p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className={`w-12 h-12 bg-${config.color}-600 rounded-lg flex items-center justify-center`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Connect {config.title}</h2>
              <p className="text-sm text-gray-600">Configure your integration credentials</p>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-blue-600" />
            How to get your credentials:
          </p>
          <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700 ml-2">
            {config.instructions.map((instruction, index) => (
              <li key={index}>{instruction}</li>
            ))}
          </ol>
          <a
            href={config.docsLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 mt-2 text-sm font-medium"
          >
            View documentation <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        {/* Form Fields */}
        <div className="space-y-4 mb-6">
          {config.fields.map((field, index) => (
            <div key={index}>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                {field.label} {field.required && <span className="text-red-500">*</span>}
              </label>
              <Input
                type={field.type}
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                placeholder={field.placeholder}
                className="bg-white border-gray-300 text-gray-900"
                required={field.required}
              />
            </div>
          ))}
        </div>

        {/* Success State */}
        {saved && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 flex items-center gap-3">
            <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
            <div className="text-sm text-green-800">
              <strong>Connected successfully!</strong> Your {config.title} integration is now active.
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between gap-4">
          <Button
            onClick={handleSaveForLater}
            variant="outline"
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Save for Later
          </Button>
          <div className="flex gap-3">
            <Button
              onClick={onClose}
              variant="ghost"
              className="text-gray-600"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={loading || !apiKey || saved}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? 'Connecting...' : saved ? 'Connected ✓' : 'Connect'}
            </Button>
          </div>
        </div>

        <p className="text-xs text-gray-500 mt-4 text-center">
          🔒 Your API keys are encrypted and stored securely. We never share your credentials.
        </p>
      </Card>
    </div>
  );
};

export default IntegrationSetupModal;
